import { Link } from 'react-router-dom';
import { Meter, StatTile } from './RpcUi';
import { compact, count, formatDate, usd } from '../lib/money';
import type { RpcSummary } from '../lib/rpc-client';

function planSourceLabel(source: string): string {
  switch (source) {
    case 'stripe':
      return 'Card subscription';
    case 'balance':
      return 'Paid from balance';
    default:
      return 'No subscription';
  }
}

/** Holds a line's height until its text arrives. */
const BLANK = ' ';

/** The overview's tiles for a self-serve plan. Without a summary yet (still
 *  loading, or before sign-in is confirmed), the same tiles at the same size,
 *  with placeholders where the numbers go. */
export function PlanStats({ s }: { s: RpcSummary | null }) {
  return (
    <section className="rpc-stats" aria-label="Account summary">
      <StatTile
        label="Plan"
        testid="stat-plan"
        value={s ? s.plan.display_name : '…'}
        sub={
          s ? (
            <>
              {planSourceLabel(s.plan_source)}
              {s.cancel_at_period_end && <> · ends {formatDate(s.period.end)}</>} ·{' '}
              <Link to="/billing">Change</Link>
            </>
          ) : (
            BLANK
          )
        }
      />
      <StatTile
        label="Units this period"
        testid="stat-units"
        value={
          s ? (
            <>
              {compact(s.units.used)}
              {s.units.included > 0 && (
                <span className="rpc-stat-of"> of {compact(s.units.included)}</span>
              )}
            </>
          ) : (
            '…'
          )
        }
        sub={s ? <>Resets {formatDate(s.period.end)}</> : BLANK}
      >
        {/* Pay as you go has no allowance to measure against. */}
        {!s ? (
          <div className="rpc-meter" aria-hidden="true" />
        ) : (
          s.units.included > 0 && (
            <Meter used={s.units.used} total={s.units.included} label="Units used this period" />
          )
        )}
      </StatTile>
      <StatTile
        label="Balance"
        testid="stat-balance"
        value={s ? usd(s.balance.cash_micros) : '…'}
        sub={
          s ? (
            <>
              {(s.plan.proofs || s.balance.proof_credit_micros > 0) && (
                <>{usd(s.balance.proof_credit_micros)} proof credit · </>
              )}
              <Link to="/billing">Add credit</Link>
            </>
          ) : (
            BLANK
          )
        }
      />
      <StatTile
        label="Rate limit"
        testid="stat-rps"
        value={s ? `${count(s.plan.requests_per_second)} req/s` : '…'}
        sub={s ? <>Per key, on your plan</> : BLANK}
      />
    </section>
  );
}
